import { Button, Spinner } from '@chopchop-se/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { setLanguage } from '../../i18n';
import { errorMessage } from '../../lib/notify';
import { completeAndroidSso, failAndroidSso } from '../android-bridge';
import { exchangeEmsToken } from '../auth-api';
import { homePathFor, useAuthStore } from '../auth-store';
import { emsAuth } from '../ems';
import { AuthCard } from './AuthCard';

/**
 * `/auth/callback` — the EMS redirect target (api default
 * `EMS_CALLBACK_URL=…/auth/callback`, DEPLOYMENT.md).
 *
 * The one job here is the session bridge, and the one rule is that the EMS JWT
 * must not outlive it:
 *
 *   1. `tryHandleCallback()` reads the token out of the URL,
 *   2. `POST /api/auth/exchange` trades it for httpOnly session cookies,
 *   3. the token variable goes out of scope and the URL is scrubbed via
 *      `replace: true` so it does not sit in history or get shoulder-surfed.
 *
 * It is never written to localStorage/sessionStorage. The old WMS did exactly
 * that, which made every XSS a permanent estate-wide identity leak.
 *
 * The guard ref matters: React 18/19 StrictMode runs effects twice in dev, and
 * exchanging the same one-time token twice fails the second time — without the
 * ref, every local sign-in would show an error on a successful login.
 */
export function CallbackPage(): ReactNode {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      const outcome = emsAuth().tryHandleCallback();
      if (!outcome.ok) {
        // Android bridge: forward the EMS error to the scanner app and stop.
        if (failAndroidSso(outcome.error.code)) return;
        setError(outcome.error.description || outcome.error.code);
        return;
      }

      // Android bridge: a pending scanner sign-in gets the EMS JWT handed to
      // the app scheme (fragment) and NO web session — the app runs its own
      // /api/auth/device-exchange. See ../android-bridge.ts.
      if (completeAndroidSso(outcome.token)) return;

      try {
        const me = await exchangeEmsToken(outcome.token);
        setSession(me);
        setLanguage(me.user.language);
        void navigate(homePathFor(me.user), { replace: true });
      } catch (cause) {
        setError(errorMessage(cause));
      }
    })();
  }, [navigate, setSession]);

  if (error) {
    return (
      <AuthCard title={t('auth.failedTitle')} subtitle={error}>
        <Button
          className="w-full"
          variant="secondary"
          onClick={() => {
            void navigate('/login', { replace: true });
          }}
        >
          {t('auth.tryAgain')}
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.signInTitle')} subtitle={t('auth.completing')}>
      <div className="flex justify-center py-4">
        <Spinner className="size-6 text-brand-600" />
      </div>
    </AuthCard>
  );
}
